import { Spacing } from '@persona-id/design-tokens';
import produce from 'immer';
import { Fragment, MutableRefObject, ReactNode, RefObject, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormSection } from 'redux-form';
import styled from 'styled-components';

import FormSectionSelfieMatch from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionSelfieMatch';
import FormSectionVideoCaptureConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionVideoCaptureConfig';
import { SectionDescription } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import DisabledFeatureConfigsTable from 'dashboard/apps/VerificationTemplateEditor/DisabledFeatureConfigPanel/DisabledFeatureConfigsTable';
import { indexForOverride } from 'dashboard/apps/VerificationTemplateEditor/DisabledFeatureConfigPanel/util';
import FormSectionCountryConfigurations from 'dashboard/apps/VerificationTemplateEditor/forms/FormSectionCountryConfigurations';
import InputMaxRetries from 'dashboard/apps/VerificationTemplateEditor/forms/InputMaxRetries';
import SelectSelfieIdCompareOption from 'dashboard/apps/VerificationTemplateEditor/forms/SelectSelfieIdCompareOption';
import { getIdCompareOption } from 'dashboard/apps/VerificationTemplateEditor/forms/SelectSelfieIdCompareOption/utils';
import useHasAccessForTierDeterminator from 'dashboard/apps/VerificationTemplateEditor/hooks/useHasAccessForTierDeterminator';
import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/VerificationTemplateEditor/hooks/useIsEditableWithOptions';
import DataSourceConfigurationTable from 'dashboard/components/DataSourceConfigurationTable/DataSourceConfigurationTable';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import FormVerificationDocumentTemplateConfig, {
  DocumentsSection,
} from 'dashboard/forms/FormVerificationDocumentTemplateConfig/FormVerificationDocumentTemplateConfig';
import FormVerificationTemplateChecks from 'dashboard/forms/FormVerificationTemplateChecks';
import VerificationTemplateHeadSection from 'dashboard/forms/FormVerificationTemplateConfig/VerificationTemplateHeadSection';
import {
  MatchRequirementKeys,
  MatchRequirementProfile,
} from 'dashboard/models/match-requirement-profile';
import { CustomerTier } from 'dashboard/models/organization';
import { isIdCompareCheck } from 'dashboard/models/verification-check';
import {
  DisabledFeatureConfig,
  IdCompareOption,
  VerificationTemplateVersion,
  VerificationTemplateVersionGovernmentId,
  VerificationTemplateVersionSelfie,
} from 'dashboard/models/verification-template-version';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { FieldSchema } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

const StepReferences = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacing[4]};
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
  width: 100%;
  padding: 0 ${Spacing[5]};
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const deleteDisabledFeatureConfig = (
  vtmplVersion: VerificationTemplateVersionGovernmentId,
  disabledFeatureConfig: DisabledFeatureConfig,
) => {
  const disabledFeatureConfigIndex = indexForOverride({
    disabledFeatureConfigs: vtmplVersion.attributes['external-disabled-feature-configs'],
    disabledFeatureConfigToFind: disabledFeatureConfig,
  });
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    draft.attributes['external-disabled-feature-configs'].splice(disabledFeatureConfigIndex, 1);
  });

  return updatedVtmplVersion;
};

interface FormVerificationTemplateConfigProps {
  vtmplVersion: VerificationTemplateVersion;
  linkedStepsElement: ReactNode;
  matchRequirementProfiles: MatchRequirementProfile[];
  formSectionName: string;
  selectedMatchRequirement: MatchRequirementKeys | null;
  selectedDisabledFeatureConfig: DisabledFeatureConfig | null;
  selectedCountryCode?: string;
  hasEditPermission: boolean;
  isFormPristine: boolean;
  hasInquiryTemplate: boolean;
  handleDisabledFeatureConfigSelected: (
    disabledFeatureConfig?: DisabledFeatureConfig | null,
  ) => void;
  handleOpenAddConfigPanel?: () => void;
  handleConfigSelected?: (countryCode: string) => void;
  handleMatchRequirementSelected: (matchRequirementKeys: MatchRequirementKeys) => void;
  handleUpdateIdCompareOption: (idCompareOption: IdCompareOption) => void;
  handleUpdateVerificationTemplateVersion: (vtmplVersion: VerificationTemplateVersion) => void;
  documentVerificationSectionRefs?: MutableRefObject<
    Record<DocumentsSection, RefObject<HTMLDivElement>>
  >;
  title?: string;
  rightColumnRef?: RefObject<HTMLDivElement>;
  fieldSchema?: FieldSchema[];
}

const FormVerificationTemplateConfig = ({
  vtmplVersion,
  linkedStepsElement,
  matchRequirementProfiles,
  formSectionName,
  selectedMatchRequirement,
  selectedDisabledFeatureConfig,
  selectedCountryCode,
  hasEditPermission,
  isFormPristine,
  hasInquiryTemplate,
  handleMatchRequirementSelected,
  handleDisabledFeatureConfigSelected,
  handleOpenAddConfigPanel,
  handleConfigSelected,
  handleUpdateIdCompareOption,
  handleUpdateVerificationTemplateVersion,
  documentVerificationSectionRefs,
  title,
  rightColumnRef,
  fieldSchema,
}: FormVerificationTemplateConfigProps) => {
  const hasAccessForFreeTier = useHasAccessForTierDeterminator()(
    EditorAccessTier.VerificationsFree,
  );
  const hasAccessForBasicTier = useHasAccessForTierDeterminator()(
    EditorAccessTier.VerificationsBasic,
  );
  const editableUngated = useIsEditableWithOptions() && hasEditPermission;
  const editableForFreeTier =
    useIsEditableWithOptions({ accessTier: EditorAccessTier.VerificationsFree }) &&
    hasEditPermission;
  const editableInternal = useIsEditableWithOptions({ internalOnly: true }) && hasEditPermission;

  const organizationIsManagedTier1OrManagedTier2 = useSelector<RootState, boolean>(
    (state) =>
      state.organization.organization?.attributes['customer-tier'] === CustomerTier.Tier1 ||
      state.organization.organization?.attributes['customer-tier'] === CustomerTier.Tier2,
  );

  const isForceEnableVtvConfigEditing = useFeatureFlag(
    'dashboard:ite-force-enable-vtv-config-editing',
  );
  const isPersonerd = useIsPersonerd();
  const canEditVerificationChecks =
    editableUngated &&
    (!organizationIsManagedTier1OrManagedTier2 || isForceEnableVtvConfigEditing || isPersonerd);

  const vtmplVersionConfigCard = useMemo<React.ReactNode>(() => {
    switch (vtmplVersion.type) {
      case ApiModel.VerificationTemplateVersionGovernmentId: {
        const vtmplVersionGovId = vtmplVersion as VerificationTemplateVersionGovernmentId;

        const handleDeleteDisabledFeatureConfig = (disabledFeatureConfig: DisabledFeatureConfig) =>
          handleUpdateVerificationTemplateVersion(
            deleteDisabledFeatureConfig(vtmplVersion, disabledFeatureConfig),
          );

        return (
          <>
            <FormSectionCountryConfigurations
              name='attributes'
              verificationTemplateVersion={vtmplVersionGovId}
              enabledIdClassesByCountry={
                vtmplVersionGovId.attributes['enabled-id-classes-by-country']
              }
              verificationChecks={vtmplVersionGovId.attributes['verification-checks']}
              accessForTierObj={{ hasAccessForFreeTier, hasAccessForBasicTier }}
              disabled={!editableForFreeTier}
              isFormPristine={isFormPristine}
            />
            <FeatureFlagged featureFlag='dashboard:ite-video-capture-config'>
              <FormSectionVideoCaptureConfig
                name='attributes'
                enabledCaptureFileTypes={vtmplVersionGovId.attributes['enabled-capture-file-types']}
                disabled={!editableForFreeTier}
              />
            </FeatureFlagged>
            <FeatureFlagged featureFlag='dashboard:enable-disabled-feature-configs'>
              <DisabledFeatureConfigsTable
                disabledFeatureConfigs={
                  vtmplVersionGovId.attributes['external-disabled-feature-configs']
                }
                selected={selectedDisabledFeatureConfig}
                onSelect={handleDisabledFeatureConfigSelected}
                onDelete={handleDeleteDisabledFeatureConfig}
                editable={editableInternal}
              />
            </FeatureFlagged>
          </>
        );
      }
      case ApiModel.VerificationTemplateVersionSelfie: {
        const vtmplVersionSelfie = vtmplVersion as VerificationTemplateVersionSelfie;
        const idCompareChecks = vtmplVersionSelfie.attributes['verification-checks']
          .filter(isIdCompareCheck)
          .map((c) => CHECK_DISPLAY_CONFIG[c.check].name)
          .sort()
          .join(', ');

        return (
          <>
            <FeatureFlagged featureFlag='dashboard:ite-video-capture-config'>
              <FormSectionVideoCaptureConfig
                name='attributes'
                enabledCaptureFileTypes={
                  vtmplVersionSelfie.attributes['enabled-capture-file-types']
                }
                disabled={!editableForFreeTier}
              />
            </FeatureFlagged>
            <Card>
              <Card.Body>
                <Card.Title>Selfie poses</Card.Title>
                <SectionDescription>Choose which pose is captured</SectionDescription>
                <FormSectionSelfieMatch name='attributes' editable={editableForFreeTier} />
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <Card.Title>ID verification comparison</Card.Title>
                <SectionDescription>
                  Choose which ID verification is used for comparison (e.g., for {idCompareChecks})
                </SectionDescription>
                <SelectSelfieIdCompareOption
                  name='attributes'
                  editable={editableForFreeTier}
                  currentIdCompareOption={getIdCompareOption(vtmplVersion)}
                  updateIdCompareOption={handleUpdateIdCompareOption}
                />
              </Card.Body>
            </Card>
          </>
        );
      }
      case ApiModel.VerificationTemplateVersionDatabase: {
        return (
          isPersonerd &&
          handleConfigSelected &&
          handleOpenAddConfigPanel && (
            <Card>
              <Card.Body
                style={{
                  position: 'relative',
                }}
              >
                <Card.Title
                  actions={
                    <Button
                      id='vendor-configs-card__add-button'
                      variant='basic'
                      onClick={() => {
                        handleConfigSelected('');
                        handleOpenAddConfigPanel();
                      }}
                    >
                      Add configuration
                    </Button>
                  }
                >
                  Data Source Configurations
                </Card.Title>
                <SectionDescription>
                  Edit how data sources are configured and prioritized on a per country basis.
                </SectionDescription>
                <DataSourceConfigurationTable
                  configs={vtmplVersion.attributes['vendor-configurations']}
                  selectedCountryCode={selectedCountryCode}
                  handleConfigSelected={handleConfigSelected}
                />
              </Card.Body>
            </Card>
          )
        );
      }
      case ApiModel.VerificationTemplateVersionPhoneNumber:
      case ApiModel.VerificationTemplateVersionEmailAddress: {
        return (
          <Card>
            <Card.Body>
              <Card.Title>Maximum retries</Card.Title>
              <SectionDescription>
                Choose the number of confirmation code attempts allowed.
              </SectionDescription>
              <InputMaxRetries name='attributes' editable={editableForFreeTier} />
            </Card.Body>
          </Card>
        );
      }
      case ApiModel.VerificationTemplateVersionDocument:
      case ApiModel.VerificationTemplateVersionAamva:
      case ApiModel.VerificationTemplateVersionBusinessWebsite:
      case ApiModel.VerificationTemplateVersionCertificateKorea:
      case ApiModel.VerificationTemplateVersionDatabaseAadhaar:
      case ApiModel.VerificationTemplateVersionDatabaseStandard:
      case ApiModel.VerificationTemplateVersionDatabaseBusiness:
      case ApiModel.VerificationTemplateVersionDatabaseBusinessFootprint:
      case ApiModel.VerificationTemplateVersionDatabaseEcbsv:
      case ApiModel.VerificationTemplateVersionDatabasePhoneCarrier:
      case ApiModel.VerificationTemplateVersionDatabaseSerpro:
      case ApiModel.VerificationTemplateVersionDatabaseTin:
      case ApiModel.VerificationTemplateVersionDatabaseVatNumber:
      case ApiModel.VerificationTemplateVersionDigitalIdFinnishTrustNetwork:
      case ApiModel.VerificationTemplateVersionDigitalIdOneId:
      case ApiModel.VerificationTemplateVersionDigitalIdSerpro:
      case ApiModel.VerificationTemplateVersionDigitalIdSingpass:
      case ApiModel.VerificationTemplateVersionDigitalIdUkSharecode:
      case ApiModel.VerificationTemplateVersionGovernmentIdNfc:
      case ApiModel.VerificationTemplateVersionMobileDriversLicense:
        break;
      default:
        assertNever(vtmplVersion);
    }
  }, [
    editableForFreeTier,
    editableInternal,
    handleConfigSelected,
    handleDisabledFeatureConfigSelected,
    handleOpenAddConfigPanel,
    handleUpdateIdCompareOption,
    handleUpdateVerificationTemplateVersion,
    hasAccessForBasicTier,
    hasAccessForFreeTier,
    isFormPristine,
    isPersonerd,
    selectedCountryCode,
    selectedDisabledFeatureConfig,
    vtmplVersion,
  ]);

  // Document verifications require a different implementation and is therefore handled within its own component
  if (vtmplVersion.type === ApiModel.VerificationTemplateVersionDocument) {
    return (
      <FormVerificationDocumentTemplateConfig
        vtmplVersion={vtmplVersion}
        linkedStepsElement={linkedStepsElement}
        matchRequirementProfiles={matchRequirementProfiles}
        formSectionName={formSectionName}
        selectedMatchRequirement={selectedMatchRequirement}
        hasEditPermission={hasEditPermission}
        hasInquiryTemplate={hasInquiryTemplate}
        handleUpdateVerificationTemplateVersion={handleUpdateVerificationTemplateVersion}
        documentVerificationSectionRefs={documentVerificationSectionRefs}
        title={title}
        rightColumnRef={rightColumnRef}
        fieldSchema={fieldSchema}
      />
    );
  }

  return (
    <RightColumn ref={rightColumnRef}>
      {title ? (
        <>
          <Spacer height={3} />
          <Heading size='sm'>{title}</Heading>
          <Spacer height={3} />
        </>
      ) : (
        <Spacer height={3} />
      )}
      <FormSection
        // when this component is being used by the InquiryTemplateEditor, the vtv will be under 'verificationTemplateVersions.id'
        // when this component is being used by the VerificationTemplateEditor, the vtv will be under 'verificationTemplateVersion'
        name={formSectionName}
        component={Fragment}
      >
        <SectionWrapper>
          <VerificationTemplateHeadSection
            vtmplVersion={vtmplVersion}
            linkedStepCallout={<StepReferences> {linkedStepsElement}</StepReferences>}
            editable={editableUngated}
          />
          <FormVerificationTemplateChecks
            vtmplVersion={vtmplVersion}
            editable={canEditVerificationChecks}
            isFormPristine={isFormPristine}
            matchRequirementProfiles={matchRequirementProfiles}
            selectedMatchRequirement={selectedMatchRequirement}
            handleMatchRequirementSelected={handleMatchRequirementSelected}
            handleUpdateVerificationTemplateVersion={handleUpdateVerificationTemplateVersion}
          />
          {vtmplVersionConfigCard}
        </SectionWrapper>
      </FormSection>
    </RightColumn>
  );
};

export default FormVerificationTemplateConfig;
